import type { FeatureCollection } from 'geojson';

import { apiClient } from './apiClient';

/** One associated pipeline in the stored per-POI breakdown. */
export interface PoiPipelineBreakdownItem {
    pipelineId: string;
    /** Raw network-function value (e.g. "TRUNK"); format with formatNetFunc for display. */
    netFunc: string | null;
    /** Length of this pipeline inside the search circle, metres, rounded to 2 dp. */
    lengthMetres: number;
}

export interface PoiPipelinesResponse {
    /** The radius the figures were calculated with, metres. */
    radiusMetres: number;
    /**
     * The POI location the calculation used. The search circle must be drawn from this
     * point, not the clicked feature's rendered coordinates (a leak entity renders at the
     * leak location, which is not the calculation centre).
     */
    centre: { longitude: number; latitude: number };
    /** Sum of the per-pipe lengths, so rows always reconcile to the total. */
    totalLengthMetres: number;
    /** One entry per associated pipeline, ordered by pipe id. */
    pipelines: PoiPipelineBreakdownItem[];
    /**
     * The associated pipelines' sections inside the circle, clipped at its boundary.
     * Feature properties are id / net_func / length (full asset length), matching the
     * viewport pipeline features.
     */
    features: FeatureCollection;
}

/**
 * Fetches the stored pipeline breakdown and clipped section geometry for a POI. Uses
 * GET /pois/{poiId}/pipelines (LeakDetectionModule policy) with the clientId override
 * query param so Origin users can view the currently selected client's data.
 *
 * One response feeds both the map radius display and the panel's Pipelines tab. Pass an
 * AbortSignal so an in-flight request is cancelled when the selected POI changes.
 */
export async function fetchPoiPipelines(
    clientId: string,
    poiId: string,
    signal?: AbortSignal,
): Promise<PoiPipelinesResponse> {
    const response = await apiClient.get<PoiPipelinesResponse>(`/pois/${encodeURIComponent(poiId)}/pipelines`, {
        params: { clientId },
        signal,
    });
    return response.data;
}

/** One stored POI-pipeline association, flattened for the export's Pipelines worksheet. */
export interface NetworkPoiPipelineRow {
    poiId: string;
    pipelineId: string;
    /** Raw network-function value (e.g. "TRUNK"); format with formatNetFunc for display. */
    netFunc: string | null;
    /** Length of this pipeline inside the POI's search circle, metres, rounded to 2 dp. */
    lengthMetres: number;
    /** The radius this association was calculated with, metres. */
    radiusMetres: number;
}

export interface NetworkPoiPipelinesDownload {
    /**
     * The client's currently configured radius, metres — stated on zero-pipe rows, which
     * have no stored association to carry their own.
     */
    radiusMetres: number;
    /** Every stored association for the client, ordered by POI then pipe id. */
    pipelines: NetworkPoiPipelineRow[];
}

/**
 * Fetches every stored POI-pipeline association for the client, for the POI download's
 * Pipelines worksheet. Uses GET /pois/network/pipelines (LeakDetectionModule policy) with
 * the clientId override query param. The server reads stored figures only — no spatial
 * calculation runs at download time.
 */
export async function fetchAllPoiPipelinesForDownload(clientId: string): Promise<NetworkPoiPipelinesDownload> {
    const response = await apiClient.get<NetworkPoiPipelinesDownload>('/pois/network/pipelines', {
        params: { clientId },
    });
    return response.data;
}
